import { useState } from "react";

import type { TeppichEingabe, TeppichErgebnis } from "@repo/teppich-calc";

import { useKalkulation } from "../../lib/kalk";
import { Ergebnisse, Kennzahl } from "./BogenRechner";
import { TextFeld, ZahlFeld } from "./Feld";
import { Zahl } from "./Zahl";

type Position = TeppichEingabe["positionen"][number] & { key: number };

let naechsteKey = 1;
const neuePosition = (p: Partial<Position> = {}): Position => ({ key: naechsteKey++, bezeichnung: "", breiteCm: 200, laengeCm: 300, umlaufCm: null, ...p });

export function TeppichRechner() {
  const [ekProQm, setEkProQm] = useState(25);
  const [ekAbschluss, setEkAbschluss] = useState(4);
  const [margeProzent, setMargeProzent] = useState(100);
  const [positionen, setPositionen] = useState<Position[]>([
    neuePosition({ bezeichnung: "Flur" }),
    neuePosition({ bezeichnung: "Bad", breiteCm: 80, laengeCm: 120, umlaufCm: 300 }),
  ]);

  const eingabe: TeppichEingabe = {
    ekProQm, ekAbschlussProLfm: ekAbschluss, marge: margeProzent / 100,
    positionen: positionen.map(({ key: _k, ...p }) => ({ ...p, umlaufCm: p.umlaufCm || null })),
  };
  const gueltig = positionen.every((p) => p.breiteCm > 0 && p.laengeCm > 0) && ekProQm > 0;
  const { ergebnis, fehler, offline } = useKalkulation<TeppichErgebnis>("teppich", eingabe, gueltig);

  const aendere = (key: number, patch: Partial<Position>) => setPositionen((alle) => alle.map((p) => (p.key === key ? { ...p, ...patch } : p)));
  const entferne = (key: number) => setPositionen((alle) => alle.filter((p) => p.key !== key));

  return (
    <div className="kalk-rechner kalk-rechner--breit">
      <div className="kalk-rechner__eingabe kalk-rechner__eingabe--zeile">
        <ZahlFeld label="EK je m²" wert={ekProQm} onChange={setEkProQm} einheit="€" min={0} schritt={0.5} />
        <ZahlFeld label="EK Abschluss je lfm" wert={ekAbschluss} onChange={setEkAbschluss} einheit="€" min={0} schritt={0.5} />
        <ZahlFeld label="Marge" wert={margeProzent} onChange={setMargeProzent} einheit="%" min={0} schritt={5} />
      </div>

      <table className="kalk-tabelle">
        <thead>
          <tr>
            <th>Position</th><th>Breite</th><th>Länge</th><th>Umlauf</th><th className="num">EK netto</th><th className="num">VK netto</th><th className="num">VK brutto</th><th aria-label="Aktion" />
          </tr>
        </thead>
        <tbody>
          {positionen.map((p, i) => {
            const e = ergebnis?.positionen[i];
            return (
              <tr key={p.key} className="kalk-zeile">
                <td><TextFeld label={<span className="kalk-unsichtbar">Bezeichnung</span>} wert={p.bezeichnung ?? ""} onChange={(v) => aendere(p.key, { bezeichnung: v })} platzhalter={`Pos. ${i + 1}`} /></td>
                <td><ZahlFeld label={<span className="kalk-unsichtbar">Breite</span>} wert={p.breiteCm} onChange={(v) => aendere(p.key, { breiteCm: v })} einheit="cm" min={1} /></td>
                <td><ZahlFeld label={<span className="kalk-unsichtbar">Länge</span>} wert={p.laengeCm} onChange={(v) => aendere(p.key, { laengeCm: v })} einheit="cm" min={1} /></td>
                <td>
                  <ZahlFeld label={<span className="kalk-unsichtbar">Abweichender Umlauf</span>} wert={p.umlaufCm ?? NaN} onChange={(v) => aendere(p.key, { umlaufCm: Number.isFinite(v) ? v : null })} einheit="cm" min={0} />
                  {e && !e.umlaufAbweichend && <small className="muted">auto {e.umlaufCm} cm</small>}
                </td>
                <td className="num">{e ? <Zahl wert={e.ekNetto} art="euro" /> : "–"}</td>
                <td className="num">{e ? <Zahl wert={e.vkNetto} art="euro" /> : "–"}</td>
                <td className="num">{e ? <Zahl wert={e.vkBrutto} art="euro" /> : "–"}</td>
                <td><button type="button" className="kalk-symbolknopf" aria-label="Position entfernen" onClick={() => entferne(p.key)} disabled={positionen.length === 1}>×</button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button type="button" className="button" onClick={() => setPositionen((alle) => [...alle, neuePosition()])} disabled={positionen.length >= 16}>
        + Position
      </button>

      <Ergebnisse fehler={fehler} offline={offline}>
        {ergebnis && (
          <>
            <Kennzahl label="Summe EK netto"><Zahl wert={ergebnis.summeEkNetto} art="euro" /></Kennzahl>
            <Kennzahl label="Summe VK netto"><Zahl wert={ergebnis.summeVkNetto} art="euro" /></Kennzahl>
            <Kennzahl label="Summe VK brutto" betont><Zahl wert={ergebnis.summeVkBrutto} art="euro" /></Kennzahl>
            <Kennzahl label="Faktor"><Zahl wert={ergebnis.faktor} /></Kennzahl>
          </>
        )}
      </Ergebnisse>
    </div>
  );
}
